import { defineStore } from 'pinia';

/**
 * @typedef {{id:string,username:string,gender:string,avatar:string,nickname:string,depId:string,orgId:string,roles:string[],perms:string[]}} UserInfo
 */

/**
 * 登录账号状态存储器
 */
export const useAuthorStore = defineStore('icloud-author-store', {
    id: 'icloud-author-store',
    state: () => ({
        token: '',
        user: {
            id: '',
            userId: '',
            username: '',
            nickname: '',
            avatar: '',
            gender: '',
            orgId: '',
            depId: '',
        },
        org: {
            id: '',
            name: '',
            creator: false,
            admin: false,
        },
        jwtTarget: {
            issuer: '', // 签发者
            subject: '', // 主题 token签发的用户类型
        },
        /** @type {string[]}*/
        roles: [], // 用户角色
        /** @type {Record<string,int>} */
        perms: {}, // 用户权限列表
    }),
    getters: {
        orgId (state) {
            return state.org?.id || '';
        },
        depId (state) {
            return state.user?.depId || '';
        },
        userId (state) {
            return state.user?.id || state.user?.userId || '';
        },
        issuer (state) {
            return state.jwtTarget?.issuer || '';
        },
        subject (state) {
            return state.jwtTarget?.subject || '';
        },
    },
    actions: {
        /**
         * 清空
         * @returns {void}
         */
        clear () {
            this.$patch((state) => {
                state.token = '';
                state.user = {
                    id: '',
                    userId: '',
                    username: '',
                    nickname: '',
                    avatar: '',
                    gender: '',
                    orgId: '',
                    depId: '',
                };
                state.roles = [];
                state.perms = {};
                state.org = {
                    id: '',
                    name: '',
                    creator: false,
                    admin: false,
                };
                state.jwtTarget = {};
            });
        },

        /**
         * 设置token签名信息
         * @param issuer {string} 发放机构
         * @param subject {string} 主题
         */
        setTarget (issuer, subject) {
            this.$patch((state) => {
                state.jwtTarget = { issuer, subject };
            });
        },

        /**
         * 设置角色及权限
         * @param roles {string[]} 用户角色列表
         * @param perms {Object} 用户权限列表
         */
        setRoles (roles, perms) {
            this.$patch((state) => {
                state.roles = [ ...roles ];
                state.perms = { ...perms };
            });
        },

        /**
         * 设置用户id
         * @param userId {string} 用户id
         */
        setUserId (userId) {
            this.user = { ...this.user, id: userId, userId };
        },
        /**
         * 设置用户机构
         * @param orgId {string} 机构id
         */
        setOrgId (orgId) {
            this.user = { ...this.user, orgId };
        },
        setOrg (org) {
            if (!org || !org.id) {
                this.$patch((state) => {
                    state.org = { id: '' };
                    state.user.orgId = '';
                });
                return;
            }
            const { id, ...more } = org || {};
            this.$patch((state) => {
                state.user = { ...state.user, orgId: id };
                state.org = { id, ...more };
            });
        },
        /**
         * 设置部门id
         * @param depId {string} 部门id
         */
        setDepId (depId) {
            this.user = { ...this.user, depId };
        },
        /**
         * 设置账户信息
         * @param author {{token:string, user: {userId:string,username:string,nickname:string,gender:string,avatar:string,orgId:string,depId:string},roles:string[],perms:Record<string,int>, org:{id:string,name:string,admin:boolean,creator:boolean,orgType:number}, issuer:string, subject:string}} [author=undefined] 账号信息
         */
        setAuthor (author = {
            token: '',
            user: {},
            roles: [],
            perms: {},
            org: {},
            issuer: 'jxpj',
            subject: 'user',
        }) {
            const {
                token = '',
                user,
                roles = [],
                perms = {},
                org,
                issuer,
                subject,
            } = author || {};
            const { userId, ...more } = user || {};
            const userRoles = new Set();
            for (const s of roles) {
                userRoles.add(s);
            }
            if (subject === 'admin') {
                userRoles.add('admin');
            } else {
                userRoles.add('user');
            }
            if (org) {
                if (org.creator) {
                    userRoles.add('org-creator');
                } else if (org.admin) {
                    userRoles.add('org-admin');
                } else {
                    userRoles.add('org-user');
                }
                const { orgType = 0 } = org;
                switch (`${orgType}`) {
                    case '2':
                        userRoles.add('consultant-user');
                        break;
                    case '3':
                        userRoles.add('budget-user');
                        break;
                    case '4':
                        userRoles.add('finance-user');
                        break;
                    default:
                        userRoles.add('company-user');
                }
            }
            this.$patch((state) => {
                state.token = token;
                state.user = { ...state.user, id: userId, ...more };
                state.roles = [ ...Array.from(userRoles) ];
                state.perms = { ...perms };
                state.org = org || {};
                state.jwtTarget = { issuer, subject };
            });
        },
        /**
         * 设置令牌
         * @param token {string} 令牌
         */
        setToken (token) {
            this.token = token;
        },
        /**
         * 设置用户
         * @param user {UserInfo}
         */
        setUser (user) {
            this.$patch((state) => {
                state.user = { ...state.user, ...user };
            });
        },
    },
    persist: {
        key: 'icloud-author-store',
        storage: localStorage,
    },
});
